import * as React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  NavLink,
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from "react-router-dom";
import {
  AppShell,
  AuthProvider,
  LoginPage,
  Spinner,
  ToastProvider,
  Toaster,
  cn,
  queryClient,
  useAuth,
  useToast,
} from "@toss-ads/ui";
import { UsersListPage } from "./pages/UsersListPage";
import { UserCreatePage } from "./pages/UserCreatePage";
import { UserDetailPage } from "./pages/UserDetailPage";
import { AdvertisersListPage } from "./pages/AdvertisersListPage";
import { AdvertiserEditPage } from "./pages/AdvertiserEditPage";
import { SecretsSourcesListPage } from "./pages/secrets/SecretsSourcesListPage";
import { SecretsKakaoListPage } from "./pages/secrets/SecretsKakaoListPage";
import { SecretsPlatformsListPage } from "./pages/secrets/SecretsPlatformsListPage";
import { AuditLogPage } from "./pages/secrets/AuditLogPage";

type StaffRole = "admin" | "developer";
const STAFF_ROLES: StaffRole[] = ["admin", "developer"];

function isStaff(role: string | undefined): role is StaffRole {
  return role === "admin" || role === "developer";
}

function LoginRoute() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) return <FullScreenSpinner />;
  if (user && isStaff(user.role))
    return <Navigate to="/users" replace />;

  return (
    <LoginPage
      appName="Skyventures Ads · 관리자"
      onLoggedIn={(me) => {
        if (isStaff(me.role)) navigate("/users", { replace: true });
      }}
    />
  );
}

function AdminNav({ role }: { role: StaffRole }) {
  // developer 만 webhook 시크릿 메뉴 노출.
  const items = [
    { to: "/users", label: "유저" },
    { to: "/advertisers", label: "광고주" },
    ...(role === "developer"
      ? [
          { to: "/secrets/sources", label: "External 소스" },
          { to: "/secrets/platforms", label: "매체 관리" },
          { to: "/secrets/kakao", label: "카카오 연동" },
          { to: "/secrets/audit", label: "변경 기록" },
        ]
      : []),
  ];
  return (
    <nav className="flex items-center gap-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            cn(
              "px-3 py-1.5 text-[13px] rounded-md transition-colors",
              isActive
                ? "bg-surface-muted text-ink font-medium"
                : "text-ink-muted hover:text-ink hover:bg-surface-muted",
            )
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function AdminProtectedRoute() {
  const { user, isLoading, logout } = useAuth();
  const toast = useToast();
  const kickedRef = React.useRef(false);

  React.useEffect(() => {
    if (
      !isLoading &&
      user &&
      !isStaff(user.role) &&
      !kickedRef.current
    ) {
      kickedRef.current = true;
      toast.warn("관리자 계정으로 로그인이 필요합니다");
      void logout();
    }
  }, [isLoading, user, logout, toast]);

  if (isLoading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff(user.role)) return <Navigate to="/login" replace />;
  return (
    <AppShell
      user={user}
      onLogout={() => void logout()}
      appName="Skyventures Ads · 관리자"
      centerSlot={<AdminNav role={user.role as StaffRole} />}
    >
      <Outlet />
    </AppShell>
  );
}

function DeveloperOnlyRoute() {
  const { user } = useAuth();
  if (user && user.role !== "developer") {
    return <Navigate to="/users" replace />;
  }
  return <Outlet />;
}

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas text-ink-subtle">
      <Spinner size={24} />
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginRoute /> },
  {
    element: <AdminProtectedRoute />,
    children: [
      { path: "/users", element: <UsersListPage /> },
      { path: "/users/new", element: <UserCreatePage /> },
      { path: "/users/:id", element: <UserDetailPage /> },

      { path: "/advertisers", element: <AdvertisersListPage /> },
      { path: "/advertisers/:id", element: <AdvertiserEditPage /> },

      {
        element: <DeveloperOnlyRoute />,
        children: [
          { path: "/secrets/sources", element: <SecretsSourcesListPage /> },
          { path: "/secrets/platforms", element: <SecretsPlatformsListPage /> },
          { path: "/secrets/kakao", element: <SecretsKakaoListPage /> },
          { path: "/secrets/audit", element: <AuditLogPage /> },
        ],
      },

      { path: "/", element: <Navigate to="/users" replace /> },
      { path: "*", element: <Navigate to="/users" replace /> },
    ],
  },
]);

void STAFF_ROLES;

export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
